import type { Feature, Geometry, GeometryCollection } from "@turf/helpers";
import type { FormikProps } from "formik";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import type { User } from "senf-shared/types";
import { toSentenceCase } from "senf-shared/utils/convertCase";
import { getMapType } from "../hooks";
import type { Result } from "../legacy/survey.types";
import type { InputRow, Page, PageType, QuestionType } from "../survey.config";
import { type MapInputProps, validMapInput } from "../utils";

const tooFew = (
  num: number,
  minChoices: Page<"choice">["settings"]["multipleChoice"]["min"],
) => num < (minChoices || Number.NEGATIVE_INFINITY);
const tooMany = (
  num: number,
  maxChoices: Page<"choice">["settings"]["multipleChoice"]["max"],
) => num > (maxChoices || Number.POSITIVE_INFINITY);
const isNullish = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "";
interface NextDisabledProps {
  pages: Page[];
  index: number;
  formik: FormikProps<Result>;
  demographicsFormik?: FormikProps<User>;
  mapInputState?: {
    featureId?: string;
    openDescription?: boolean;
  };
}
export const useNextDisabled = ({
  pages,
  index,
  formik,
  demographicsFormik,
  mapInputState,
}: NextDisabledProps) => {
  const { t, i18n } = useTranslation();
  const message = useRef<string | boolean>("");
  let disabled = false;

  const page = pages[index] as Page<PageType>;
  if (!page) {
    return { disabled, message };
  }
  const { options, type: qtype, uid } = page || {};
  const { rows } = options || {};
  // const {
  //   required,
  //   textResponse,
  //   textResponseRequired,
  //   minChoices,
  //   maxChoices,
  //   minValue,
  //   maxValue,
  //   maxChar,
  //   multipleChoice,
  //   mapbasedQtype,
  //   mapbasedFeatureLimits,
  //   minFeatures,
  //   maxFeatures,
  // } = page?.settings || {};

  // if (!required || !qtype) {
  //   return {
  //     disabled,
  //     message,
  //   };
  // }
  const { value, comment } = formik.values[uid] || {};
  const valueArray = Object.values(value || {}) as (string | number | null)[];
  // Helper function to safely check multipleChoice.enabled
  const getMultipleChoiceEnabled = (page: Page<PageType>) => {
    if (
      page.type === "choice" ||
      page.type === "choiceImage" ||
      page.type === "choiceMatrix"
    ) {
      return page.settings?.multipleChoice?.enabled;
    }
    if (
      (page.type === "mapPoint" ||
        page.type === "mapPath" ||
        page.type === "mapArea") &&
      page.settings?.valueType === "boolean"
    ) {
      return page.settings?.multipleChoice?.enabled;
    }
    return false;
  };

  const numChoices = getMultipleChoiceEnabled(page)
    ? value?.length || 0
    : value
      ? 1
      : 0;
  const numChoicesValueArray = valueArray?.length || 0;

  const schema: Record<
    PageType | "map",
    (
      value?: string | Record<string, string | string[] | { value: string }>,
    ) => boolean | string
  > = {
    demographics: () => {
      if (!demographicsFormik?.isValid) {
        try {
          return toSentenceCase(
            new Intl.ListFormat(i18n.resolvedLanguage, {
              style: "long",
              type: "conjunction",
            }).format(Object.values(demographicsFormik?.errors || {})),
          );
        } catch (error) {
          return t("global:fillInAllFields");
        }
      }
      return false;
    },
    textResponse: (value) => {
      if (page.type !== "textResponse") return false;
      if (
        page.settings?.required &&
        (rows?.length || 0) === 1 &&
        rows?.some((e) => !value?.[e?.uid]?.value?.length)
      ) {
        return t("survey:disabledMessages.textResponse");
      }
      if (
        page.settings?.required &&
        (rows?.length || 0) > 1 &&
        rows?.some((e) => !value?.[e?.uid]?.value?.length)
      ) {
        return t("survey:disabledMessages.textResponse.everyRow");
      }
      if (
        page.settings?.limitCharacters?.minChars &&
        rows?.some(
          (e) =>
            value?.[e.uid]?.value?.length &&
            (value?.[e.uid]?.value?.length || 0) <
              (page.settings?.limitCharacters?.minChars || 0),
        )
      )
        return t("survey:disabledMessages.textResponse.minChars", {
          minChars: page.settings?.limitCharacters?.minChars,
        });

      /* @todo this is not strictly necessary because the input prevents input of more than maxChars though maxLength prop */
      if (
        page.settings?.limitCharacters?.maxChars &&
        rows?.some(
          (e) =>
            (value?.[e.uid]?.value?.length || 0) >
            (page.settings?.limitCharacters?.maxChars || 999999),
        )
      )
        return t("survey:disabledMessages.textResponse.maxChars", {
          maxChar: page.settings?.limitCharacters?.maxChars,
        });
      return false;
    },
    numberInput: (value) => {
      if (page.type !== "numberInput") return false;
      const { required, limit } = page.settings || {};
      const valueArray = Object.values(value || {}).map((e) => e?.value);
      const numChoices = rows?.length || 1;
      const [min, max] = [limit?.min, limit?.max];

      if (required && rows?.some((e) => !value?.[e?.uid]?.value)) {
        if (numChoices > 1)
          return t("survey:disabledMessages.numberInput.everyRow");
        return t("survey:disabledMessages.numberInput");
      }
      if (
        !isNullish(min) &&
        valueArray.some((e) => (!isNullish(e) || required) && e < min)
      ) {
        return t("survey:disabledMessages.numberInput.min", { min });
      }
      if (
        !isNullish(max) &&
        valueArray.some((e) => (!isNullish(e) || required) && e > max)
      ) {
        return t("survey:disabledMessages.numberInput.max", { max });
      }
      if (
        valueArray.some(
          (e) =>
            (!isNullish(e) || required) &&
            ((!isNullish(min) && e < min) || (!isNullish(max) && e > max)),
        )
      ) {
        return t("survey:disabledMessages.numberInput.minMax", {
          min,
          max,
        });
      }
      return false;
    },
    budgetAllocation: () => {
      if (page.type !== "budgetAllocation") return false;
      return (
        !!page.settings?.required &&
        (!value || !numChoices) &&
        t("survey:disabledMessages.checkbox")
      );
    },
    choice: (value) => {
      if (page.type !== "choice" && page.type !== "choiceImage") return false;
      const { enabled, min, max } = page.settings?.multipleChoice || {};
      const numChoices = enabled
        ? value?.length
          ? Number(value.length)
          : 0
        : value
          ? 1
          : 0;

      if (page.settings?.required && !numChoices)
        return t("survey:disabledMessages.choice");
      if (tooFew(numChoices, min))
        return t("survey:disabledMessages.choice.minChoices", {
          count: min,
        });
      // THE DISABLED LOGIC IS MOVED TO THE RADIO
      if (tooMany(numChoices, max))
        return t("survey:disabledMessages.choice.maxChoices", {
          count: max,
        });
      return false;
    },
    choiceMatrix: (value) => {
      if (page.type !== "choiceMatrix") return false;
      const { required, multipleChoice } = page.settings || {};
      const valueArray = Object.values(value || {});
      const numChoicesValueArray = valueArray?.length || 0;

      const { min, max } = page.settings?.multipleChoice || {};

      if (
        required &&
        (!valueArray || numChoicesValueArray < (rows?.length || 0))
      )
        return t("survey:disabledMessages.choiceMatrix");
      if (
        multipleChoice?.enabled &&
        valueArray.some((e) => tooFew(e.length, min))
      )
        return t("survey:disabledMessages.choiceMatrix.minChoices", {
          count: min,
        });
      // THE DISABLED LOGIC IS MOVED TO THE choiceMatrix
      if (
        multipleChoice?.enabled &&
        valueArray.some((e) => tooMany(e.length, max))
      )
        return t("survey:disabledMessages.choiceMatrix.maxChoices", {
          count: max,
        });
      return false;
    },
    ratingScale: () => {
      if (
        page.type !== "ratingScale" &&
        page.type !== "ratingStars" &&
        page.type !== "ratingSlider"
      )
        return false;

      const { required, textResponse } = page.settings || {};
      const { maxChars } = textResponse || {};
      // if (!value || !Object.values(value).some(Boolean))
      if (
        required &&
        (!valueArray || numChoicesValueArray < (rows?.length || 0))
      )
        return t("survey:disabledMessages.ratingScale");
      if (textResponse?.required && !comment)
        return t("survey:disabledMessages.ratingScale.textarea");
      if (maxChars && comment?.length > maxChars)
        return t("survey:disabledMessages.textResponse.maxChar", {
          maxChar: maxChars,
        });
      return false;
    },
    map: (type: "mapPoint" | "mapPath" | "mapArea") => {
      if (
        page.type !== "mapPoint" &&
        page.type !== "mapPath" &&
        page.type !== "mapArea"
      )
        return false;

      const { required, valueType, mapResponse } = page.settings || {};
      const { enabled, min, max } = mapResponse?.limit || {};

      const geoData = formik.values[uid]?.geoData;
      const geoDataObj = geoData && JSON.parse(geoData);
      const features = geoDataObj?.features as Feature<
        Geometry | GeometryCollection,
        MapInputProps
      >[];
      const getFeatureType = (pageType: Page, count: number) => {
        const type = getMapType(pageType);
        return t(count === 1 ? `global:${type}` : `global:${type}s`);
      };

      if (required && (!geoData || !features) && !enabled) {
        return t(`survey:disabledMessages.${type}`);
      }

      if (
        required &&
        valueType !== "none" &&
        features &&
        !validMapInput(features, valueType)
      ) {
        return t("global:addAllRequiredData");
      }

      if (
        required &&
        (!features?.length || (enabled && (min || 0) > features?.length))
      ) {
        const minMaxFeatureKey =
          min && max && min === max
            ? "youMustAddExactlyFeatures"
            : min && max
              ? "youMustAddMinMaxFeatures"
              : !min && max
                ? "youMustAddMaxFeatures"
                : min && !max
                  ? "youMustAddMinFeatures"
                  : "youCanAddMultipleFeatures";

        return t(`survey:options.${minMaxFeatureKey}`, {
          count: !enabled ? 1 : min || max || 2,
          maxCount: max,
          featureType: getFeatureType(
            { type } as Page<PageType>,
            !enabled ? 2 : max || min || 2,
          ),
        });
      }

      const {
        enabled: limitCharactersEnabled,
        minChars,
        maxChars,
      } = (page.settings.valueType === "string" &&
        page.settings.limitCharacters) ||
      {};

      if (
        required &&
        page.settings.valueType === "string" &&
        limitCharactersEnabled
      ) {
        if (
          minChars &&
          features?.some(
            (e) => (e.properties.description?.length || 0) < minChars,
          )
        ) {
          return t("survey:disabledMessages.textResponse.minChars", {
            minChars,
          });
        }
        if (
          maxChars &&
          features?.some(
            (e) => (e.properties.description?.length || 0) > maxChars,
          )
        ) {
          return t("survey:disabledMessages.textResponse.maxChars", {
            maxChars,
          });
        }
      }

      //THIS CAN ACTUALLY NEVER HAPPEN
      if (enabled && max && max < features?.length) {
        return t("survey:disabledMessages.map.maxFeatures", {
          maxFeatures: max,
          featureType: getFeatureType(
            { type } as Page<PageType>,
            !enabled ? 2 : max || min || 2,
          ),
        });
      }

      if (mapInputState?.featureId) {
        return t("survey:disabledMessages.featureId");
      }
      if (mapInputState?.openDescription) {
        return t("survey:disabledMessages.textResponse.everyRow");
      }

      return false;
    },
  };

  const schemaMap = {
    ...schema,
    choiceImage: schema.choice,
    ratingScale: schema.ratingScale,
    ratingStars: schema.ratingScale,
    ratingSlider: schema.ratingScale,
    mapPoint: () => schema.map("mapPoint"),
    mapPath: () => schema.map("mapPath"),
    mapArea: () => schema.map("mapArea"),
  };

  message.current = schemaMap[qtype]?.(value);
  disabled = typeof message.current === "string" && !!message.current.length;

  return { disabled, message };
};
